import { DateTime } from 'luxon';
import { ScheduleTime } from '../../../API';
import { EASTERN_TIME_ZONE } from '../../../utils/dateHelpers';

/**
 * `HH:mm:ss` is the Eastern wall clock the editor stored. `HH:mm:ssZ` (or an explicit offset) is a
 * legacy instant. Passing `zone` here is what keeps those apart: it applies only when the string
 * has no offset, and a string that already has one keeps that instant, expressed in Eastern.
 *
 * Parsing in the viewer's zone and moving the result to Eastern afterwards does the opposite to a
 * legacy value. From Bogotá, `11:55:00Z` is already 06:55 locally, and stamping those digits onto
 * Eastern shows 06:55 instead of 07:55.
 */
export const parseValueToDateTime = (val?: string): DateTime | string | undefined => {
  if (!val) return val;
  return DateTime.fromISO(val, { zone: EASTERN_TIME_ZONE });
};

export const parseValueToTime = (val?: string): string | undefined => {
  return !val ? val : (parseValueToDateTime(val) as DateTime)?.toFormat('hh:mm a');
};

export function isInScheduledTime(schedules?: ScheduleTime[]): boolean {
  if (!schedules || schedules.length === 0) return true;
  const localNow = parseValueToDateTime(DateTime.now().toISO() as string) as DateTime;
  const localNowTime = localNow.toFormat('T'); // Extracts the time as 'HH:mm:ss.sss'
  const localNowDay = localNow.weekday; // Gets the day of the week (1 = Monday, 7 = Sunday)

  return schedules.some((schedule) => {
    // Check if the current day of the week is in the schedule's daysOfWeek
    if (schedule.daysOfWeek.includes(localNowDay)) {
      const startLocalTime = (parseValueToDateTime(schedule.startTime) as DateTime)?.toFormat('T');
      const endLocalTime = (parseValueToDateTime(schedule.endTime) as DateTime)?.toFormat('T');
      // Check if the current local time is between the start and end times of the schedule
      return localNowTime >= startLocalTime && localNowTime <= endLocalTime;
    }
    return false;
  });
}

export function getActiveEndTime(schedules?: ScheduleTime[]): string {
  if (!schedules || schedules.length === 0) return '';
  const localNow = parseValueToDateTime(DateTime.now().toISO() as string) as DateTime;
  const localNowTime = localNow.toFormat('T'); // Extracts the time as 'HH:mm:ss.sss'
  const localNowDay = localNow.weekday; // Gets the day of the week (1 = Monday, 7 = Sunday)

  const activeSchedule = schedules.find((schedule) => {
    // Check if the current day of the week is in the schedule's daysOfWeek
    if (schedule.daysOfWeek.includes(localNowDay)) {
      const startLocalTime = (parseValueToDateTime(schedule.startTime) as DateTime)?.toFormat('T');
      const endLocalTime = (parseValueToDateTime(schedule.endTime) as DateTime)?.toFormat('T');
      // Check if the current local time is between the start and end times of the schedule
      return localNowTime >= startLocalTime && localNowTime <= endLocalTime;
    }
    return false;
  });

  if (activeSchedule) {
    return (parseValueToDateTime(activeSchedule.endTime) as DateTime)?.toFormat('hh:mm a');
  }
  return '';
}
